/*
  Reveal/replay trigger for the shared tasting-trait icons on the product
  page's featured-icon row (snippets/wakura-flavour.liquid,
  .product-page__tasting-highlights). All actual motion is declared in
  assets/wakura-trait-icon-motion.css, gated on `animation-name` and pinned
  to `none` by default — this file's only job is deciding WHEN to promote
  that name via .wk-trait-icon--armed / .is-visible. If it never runs, every
  icon just renders its own plain static state; there is nothing to recover
  from.

  ── Three ways an icon's animation gets triggered ─────────────────────────

  1. First reveal (scroll): the row is observed, not each icon individually.
     Reaching ~50% visible arms the whole row and starts a 300ms dwell
     timer; leaving that threshold before the timer fires cancels it (and
     un-arms nothing extra to undo, since nothing is armed before the dwell
     actually succeeds — see "arm late" below). On success, the icons reveal
     in sequence with a ~100ms stagger, using their own already-approved
     per-icon animation untouched. This can only ever happen once per page
     view (see firstRevealDone) and then the row's observer disconnects.

  2. Tab replay: returning to the Flavour tab after leaving it replays the
     same staggered sequence, every time, regardless of whether the scroll
     reveal already happened. Detected via a MutationObserver on the
     existing Flavour panel's `hidden` attribute
     (.wk-product-tabs__panel--flavour) — assets/wakura-product.js already
     flips that attribute in activate() and dispatches no custom event, so
     this is the smallest possible hook: zero changes to wakura-product.js
     or wakura-tabs.liquid. A MutationObserver only reports attribute
     changes made AFTER it starts observing, so the page-load default-open
     state (Flavour starts unhidden) never counts as a "return to Flavour" —
     only a genuine hidden -> visible transition does, which requires having
     left the tab first.

  3. Hover replay (CSS only, in wakura-trait-icon-motion.css): once a
     sequence's icons finish animating, THIS file removes .is-visible from
     them (see the cleanup timer in playSequence) so each icon's
     animation-name reverts to the CSS base of `none` at rest. That is what
     lets the hover rule replay an icon indefinitely — every hover-in is
     then a genuine none -> name transition, which creates a fresh animation
     each time, rather than a no-op re-assignment of an already-set name.

  ── Arm late, not speculatively ────────────────────────────────────────────

  "Armed" (.wk-trait-icon--armed) pauses an animation at frame 0, which for
  a stroke-draw icon means fully hidden. The row-visibility observer never
  arms anything itself — it only manages the 300ms timer. Arming happens
  inside playSequence(), i.e. only once a sequence is about to actually run
  (dwell succeeded, or an explicit tab return, or the no-IntersectionObserver
  fallback), never while merely deciding whether one might. That keeps the
  icons in their normal static-visible state for the entire dwell window,
  and only the brief run of the sequence itself ever hides them.

  ── State ──────────────────────────────────────────────────────────────────
    firstRevealDone     — true after the scroll-triggered sequence has run
                           once (or immediately, if there's no
                           IntersectionObserver at all). Never reset. Tab
                           replay does not check or set this on its own path
                           logically, but a successful play from ANY trigger
                           marks it true (see playSequence) so the scroll
                           observer never re-fires after a tab-triggered play
                           has already shown the shopper the sequence.
    fullSequenceRunning — guards playSequence() itself against overlap, so a
                           dwell-triggered play and a tab-replay can never
                           run at the same time, and rapid repeated triggers
                           collapse into a no-op rather than stacking.
    pendingRevealTimer  — the in-flight 300ms dwell timer, or null. Cleared
                           (and the timer cancelled) the moment the row drops
                           back below the visibility threshold.
*/
(function () {
  var VISIBILITY_THRESHOLD = 0.5;
  var DWELL_MS = 300;
  var STAGGER_MS = 150;
  /* Generous ceiling above the documented per-icon animation total
     (delay + duration lands roughly 1000-1750ms across the set, per the
     --wk-step/--wk-dur/--wk-draw/--wk-draw-slow tokens in
     wakura-trait-icon-motion.css — Aromatic's second curve is the longest
     at ~1740ms) — used only to decide when it's safe to drop .is-visible
     for hover hand-off; being a little late has zero visible effect, since
     animation-fill-mode: both already holds the finished frame regardless. */
  var ICON_SETTLE_MS = 1900;

  var initialized = false;
  var firstRevealDone = false;
  var fullSequenceRunning = false;
  var pendingRevealTimer = null;
  var rowObserver = null;
  var tabObserver = null;

  function reveal(icon) {
    /* Unarm and run in one go: an armed icon is paused at frame 0, so these
       two class changes must never be separated. */
    icon.classList.remove('wk-trait-icon--armed');
    icon.classList.add('is-visible');
  }

  /* Plays the full featured-icon sequence once, staggered ~100ms apart,
     using each icon's own already-approved animation untouched. Shared by
     the scroll-dwell trigger, the tab-replay trigger, and the
     no-IntersectionObserver fallback — none of them touch icon classes
     directly, so there is exactly one place this can go wrong. */
  function playSequence(row) {
    if (fullSequenceRunning) return;
    var icons = Array.prototype.slice.call(row.querySelectorAll('.wk-trait-icon'));
    if (!icons.length) return;

    fullSequenceRunning = true;
    /* Any successful play counts as "the shopper has seen this" — including
       one that started from a tab return before the scroll dwell ever
       finished — so the scroll observer (if still connected) won't also
       fire a redundant first-time play later. */
    firstRevealDone = true;

    /* Hide all of them together, right as the sequence starts — not
       earlier, and not one at a time — so nothing sits speculatively armed
       while merely waiting its turn, and no icon flashes visible-then-
       hidden independently of the others. */
    icons.forEach(function (icon) {
      icon.classList.add('wk-trait-icon--armed');
    });
    icons.forEach(function (icon, i) {
      setTimeout(function () {
        reveal(icon);
      }, i * STAGGER_MS);
    });

    var totalTime = (icons.length - 1) * STAGGER_MS + ICON_SETTLE_MS;
    setTimeout(function () {
      /* Drop .is-visible once the sequence has certainly finished, so each
         icon's animation-name falls back to the CSS base of `none` at
         rest — this is what makes hover replay work afterward (see the
         file header). Visually identical to leaving it in place, since the
         animation already holds its finished frame either way. */
      icons.forEach(function (icon) {
        icon.classList.remove('is-visible');
      });
      fullSequenceRunning = false;
    }, totalTime);
  }

  /* Observes the row (not each icon) and manages only the 300ms dwell timer
     — no class is touched here at all. Disconnects itself permanently after
     one successful dwell, since the scroll-triggered reveal is a once-only
     event for the page view. */
  function setupRowObserver(row) {
    if (!('IntersectionObserver' in window)) {
      /* No way to measure "how much of the row is visible," so there is
         nothing to gate on — reveal immediately rather than leaving the
         icons stuck deciding. */
      playSequence(row);
      return;
    }

    rowObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (firstRevealDone) {
          rowObserver.disconnect();
          return;
        }

        var visibleEnough = entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;

        if (visibleEnough) {
          if (pendingRevealTimer) return; // already dwelling
          pendingRevealTimer = setTimeout(function () {
            pendingRevealTimer = null;
            playSequence(row);
            rowObserver.disconnect();
          }, DWELL_MS);
        } else if (pendingRevealTimer) {
          /* Dropped back below the threshold before the dwell completed —
             cancel outright. Nothing was armed yet (playSequence is the
             only place that arms), so there is nothing else to undo. */
          clearTimeout(pendingRevealTimer);
          pendingRevealTimer = null;
        }
      });
    }, { threshold: [0, VISIBILITY_THRESHOLD] });

    rowObserver.observe(row);
  }

  /* Replays the full sequence whenever the shopper explicitly returns to
     the Flavour tab. Hooks the EXISTING `hidden` attribute that
     assets/wakura-product.js's activate() already toggles on the Flavour
     panel — no change to that file or to wakura-tabs.liquid. */
  function setupTabReplay(row) {
    var panel = document.querySelector('.wk-product-tabs__panel--flavour');
    if (!panel || !('MutationObserver' in window)) return;

    tabObserver = new MutationObserver(function () {
      if (panel.hidden) return; // just left Flavour, not a return to it
      playSequence(row);
    });
    tabObserver.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
  }

  function init() {
    if (initialized) return; // idempotent — safe to call more than once

    /* Opt out entirely rather than setting anything up and relying on CSS
       to cancel it — this way no class is ever added, no observer is ever
       created, and no icon can be caught mid-sequence. */
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var row = document.querySelector('.wakura-product .product-page__tasting-highlights');
    if (!row) return; // no featured icons on this product — nothing to do

    initialized = true;
    setupRowObserver(row);
    setupTabReplay(row);
  }

  /* Exposed for parity with the rest of this theme's re-entrant init()
     pattern (e.g. a section re-render from the theme editor). The
     `initialized` guard makes repeat calls a no-op once genuinely set up. */
  window.wkTraitIconMotion = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
